import React, { useState } from 'react';
import './TeacherPhoto.css';

/**
 * A teacher's picture, or their initials when there is no picture.
 *
 * Profiles are entered by the office, and a photograph is the one field it
 * cannot always supply on the day somebody is hired. An <img> with an empty or
 * dead src draws the browser's broken-image icon, which looks like the site is
 * failing rather than like a photo that has not arrived yet — so when there is
 * nothing to show, this draws the initials on the brand navy instead.
 *
 * It also catches a src that turns out to be dead after loading starts, which
 * is the ordinary fate of a link pasted from somewhere else.
 *
 * Shape is the other half of the job. Every hole the site draws a teacher in is
 * a circle whose width and height are fixed in the page's own stylesheet, and
 * an <img> given both without `object-fit` is *stretched* to fill them — a
 * portrait taller than it is wide arrives with a widened face. So the picture
 * carries its own `teacher-photo` class, which crops instead of stretching.
 *
 * Cropping then has to choose what to drop, and dead centre is the wrong answer
 * often enough to matter: in a half-length photo the face sits in the upper
 * third, so a centred square crop keeps the chest and cuts the forehead. The
 * frame is therefore per-photo. `position` takes any CSS object-position value
 * — "50% 30%" pulls the frame up towards the face — and is read from the
 * teacher's own `photo_position` field, so one face sitting high in its frame
 * does not drag every other face on the page with it. Unset, it falls back to
 * the centred default in the stylesheet, which is right for a head-and-
 * shoulders portrait and is what the photo script already aims for.
 */

function initials(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();
}

export default function TeacherPhoto({ src, name, className = '', position }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className={`teacher-photo-fallback ${className}`} role="img" aria-label={name}>
        <span>{initials(name)}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className={`teacher-photo ${className}`}
      style={position ? { objectPosition: position } : undefined}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}
